import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from '@base/axios-base-query'
import jsonApiNormalize from '@base/json-api-normalizer.js'
import { receiveEntities } from '@actions/entity_actions'
import { fieldSelectOptionDeleted } from '@actions/field_select_option_actions'
import { serializableError } from '@utils/serializable_error'
import {
  fetchFieldSelectOptions as fetchFieldSelectOptionsRequest,
  createFieldSelectOption as createFieldSelectOptionRequest,
  updateFieldSelectOption as updateFieldSelectOptionRequest,
  deleteFieldSelectOption as deleteFieldSelectOptionRequest,
} from '@api/FieldSelectOptionWebAPI'
import { AxiosError } from 'axios'
import { createLogger } from '@utils/logger'

const log = createLogger('fieldSelectOptionsApi')
log.setLevel('info')

export interface FieldSelectOption {
  id: number
  label: string
}

// we usually need the tableId and fieldId
export interface BaseFieldSelectOptionArgs {
  tableId: string | number
  fieldId: string | number
}

export interface FetchFieldSelectOptionsArgs extends BaseFieldSelectOptionArgs {
  tableId: string | number
  fieldId: string | number
}

export interface AddFieldSelectOptionArgs extends BaseFieldSelectOptionArgs {
  label: string
}

export interface UpdateFieldSelectOptionArgs extends BaseFieldSelectOptionArgs {
  optionId: number
  label?: string
  position?: number
}

export interface RemoveFieldSelectOptionArgs extends BaseFieldSelectOptionArgs {
  optionId: number
}

export const fieldSelectOptionsApi = createApi({
  reducerPath: 'fieldSelectOptionsApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/' }),
  endpoints: build => ({
    fetchOptions: build.query<FieldSelectOption[], FetchFieldSelectOptionsArgs>({
      queryFn: async ({ tableId, fieldId }) => {
        try {
          const response = (await fetchFieldSelectOptionsRequest({ tableId, fieldId })) as any
          // return in correct format
          return { data: response.data.data }
        } catch (error) {
          log.error('Error fetching field select options:', error)
          return { error: serializableError(error) }
        }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        // also ensure our normalized data slice gets updated when we fetch options
        try {
          const result = await queryFulfilled
          const normalized = jsonApiNormalize(result, { camelizeKeys: false, camelizeTypeValues: false })
          log.debug('Normalized fetched field select options:', normalized)
          dispatch(receiveEntities(normalized))
        } catch {
          // no-op on error
          log.error('Error normalizing fetched field select options')
        }
      },
    }),
    addOption: build.mutation<FieldSelectOption, AddFieldSelectOptionArgs>({
      queryFn: async ({ tableId, fieldId, label }) => {
        try {
          const response = await createFieldSelectOptionRequest({ tableId, fieldId, label })
          // Note: Field is in response.data.data
          const fieldSelectOption = response.data.data
          return { data: fieldSelectOption } // Properly return the response data
        } catch (error) {
          const axiosError = error as AxiosError
          return { error: serializableError(axiosError) }
        }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        // also ensure our normalized data slice gets updated when we add an option
        try {
          const result = await handleAxiosError(queryFulfilled)
          const normalized = jsonApiNormalize(result, { camelizeKeys: false, camelizeTypeValues: false })
          log.debug('Normalized added field select option:', normalized)
          dispatch(receiveEntities(normalized))
        } catch {
          log.error('Error normalizing added field select option')
        }
      },
    }),
    updateOption: build.mutation<FieldSelectOption, UpdateFieldSelectOptionArgs>({
      queryFn: async ({ tableId, fieldId, optionId, label, position }) => {
        try {
          const response = (await updateFieldSelectOptionRequest({
            tableId,
            fieldId,
            optionId,
            label,
            position,
          })) as any

          log.debug('Response from updating field select option:', response)
          // Note: Field is in response.data.data
          const fieldSelectOption = response.data.data
          return { data: fieldSelectOption } // Properly return the response data
        } catch (error) {
          const axiosError = error as AxiosError
          return { error: serializableError(axiosError) }
        }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          // also ensure our normalized data slice gets updated when we update an option
          const result = await handleAxiosError(queryFulfilled)
          const normalized = jsonApiNormalize(result, { camelizeKeys: false, camelizeTypeValues: false })
          log.debug('Normalized updated field select option:', normalized)
          dispatch(receiveEntities(normalized))
        } catch {
          log.error('Error normalizing updated field select option')
        }
      },
    }),
    removeOption: build.mutation<any, RemoveFieldSelectOptionArgs>({
      queryFn: async ({ tableId, fieldId, optionId }) => {
        try {
          const response = await deleteFieldSelectOptionRequest({ tableId, fieldId, optionId })
          console.log('Response from deleting field select option:', response)
          return { data: response.data }
        } catch (error) {
          const axiosError = error as AxiosError
          return { error: serializableError(axiosError) }
        }
      },
      async onQueryStarted({ optionId }, { dispatch, queryFulfilled }) {
        try {
          // also ensure our normalized data slice gets updated when we delete an option
          const result = await handleAxiosError(queryFulfilled)

          // remove the option from the entities slice
          dispatch(receiveEntities({ field_select_options: { [optionId]: null } }))

          // Normalize the deleted option data
          const normalized = jsonApiNormalize(result.data, { camelizeKeys: false, camelizeTypeValues: false }) as any
          log.debug('Normalized deleted field select option:', normalized)
          if (Object.keys(normalized.field_select_options).length) {
            dispatch(fieldSelectOptionDeleted(normalized))
          }
        } catch (e) {
          log.error('Error normalizing deleted field select option', e)
        }
      },
    }),
  }),
})

export const { useFetchOptionsQuery, useAddOptionMutation, useRemoveOptionMutation, useUpdateOptionMutation } =
  fieldSelectOptionsApi

// Wrap axios errors in serializableError for all mutations
const handleAxiosError = async (queryFulfilled: any) => {
  try {
    const { data } = await queryFulfilled
    return { data }
  } catch (error) {
    return { error: serializableError(error) }
  }
}
